import React from "react";
import {DateTime, Image, Mail, Number, ReadOnlyText, Select, Text, YesNo} from "../components/forms";
import {CheckCell, DateCell, MultiTextCell, StateCell, StoreAddressCell, TextCell, YesNoCell} from "../components/grids";
import {EntitiesLookupContainer, ValuesLookupContainer, ValuesSelectContainer} from "../components/containers";
import M from "../../strings";
import {getLoggedUser, hasPermission} from "../../api/session";
import {resetUserPassword} from "../../actions/account";
import * as ui from "../utils/ui";
import {logout} from "../../actions/session";
import {activeSearchForm} from "../screens/entities/commonFields";
import { SEARCH_FORM_CODE_DESCRIPTOR, SEARCH_FORM_DATE_DESCRIPTOR } from "../../model/searchForms";
import { isEmpty } from "../../framework/utils";
import { Query } from "../../framework/query";
import * as datasource from "../../utils/datasource";
import { ComputationType, RatingType, TargetType } from "../../model/enums";
import { mobileConfirm, swal } from "../../plugins";
import { CategoriesTreeSelector, CategoriesTreeSelectorControl } from "../components/categoriesTreeSelectorControl";
import { showDialog } from "../../actions/dialog";
import { Wizard } from "../components/wizard";
import { optional } from "../../utils/lang";
import moment from "moment";
import { PresentationNotesControl, PresentationNotesTree } from "../components/presentationNotesControl";
import { getFileServerUrl } from "../../framework/utils"
import ArrowTrend from "../components/mobile/arrowTrend";
import { DATE_FORMAT_FULL } from "../../utils/date";
import { formatStoreAddress } from "../utils/retailers";
import { formatObservations } from "../utils/surveys";
import { EvaluationsGridHeader } from "../components/evaluationsGridHeader";
import { RetailersGridHeader } from "../components/retailersGridHeader";

const entities = {
	user: {
		grid: {
			quickSearchEnabled: true,
			title: M("usersList"),
			subtitle: M("usersListDescription"),
			descriptor: {
				columns: [
					{property: "code", header: M("code"), cell: TextCell, sortable: true, searchable: true, searchForm: SEARCH_FORM_CODE_DESCRIPTOR()},
					{property: "name", header: M("name"), cell: TextCell, sortable: true, searchable: true},
					{property: "lastname", header: M("lastname"), cell: TextCell, sortable: true, searchable: true},
					{
						property: "registrationDate", 
						header: M("registrationDate"), 
						cell: DateCell, 
						sortable: true, 
                        searchable: true, 
                        searchForm: SEARCH_FORM_DATE_DESCRIPTOR("registrationDate"),
                    },
					{property: "mail", header: M("mail"), cell: TextCell, sortable: true, searchable: true},
					{property: "roles", header: M("roles"), cell: TextCell, sortable: false, searchable: false, props: { formatter: v => v.map(r => r.role).join(", ")}},
					{
						property: "active",
						header: M("active"),
						cell: CheckCell,
						sortable: true,
						searchable: true,
						searchForm: activeSearchForm
					}
				]
			}
		},
		form: {
			getTitle(data, params) {
				return [
					{title: M("usersList"), url: "/entities/user" },
					{title: !data || !data.id ? M(["create", "user"]) : M(["edit", "user"]) + " <b>" + data.fullDescription + "</b>"},
				]
			},
			getActions(data) {
				let actions = ["back", "save", "save-go-back", "revisions"];
				if (hasPermission("canResetPassword")) {
					if (data && data.id) {
						actions.push({
							type: "icon",
							icon: "zmdi zmdi-brush",
							tooltip: "Reset password",
							action: () => {
								mobileConfirm(M("confirm"), "Verrà impostata una nuova password ed inviata all'indirizzo mail dell'utente")
									.then(() => {
										resetUserPassword({id: data.id})
										if (data.id === getLoggedUser().id) {
											swal({
												title: M("confirm"),
												text: "La tua password è stata resettata. Dovrai eseguire un nuovo accesso",
												showCancelButton: false
											})
												.finally((res) => {
													logout();
													ui.navigate("/login")
												})
										}
									})
									.catch((e) => {
										logger.i(e)
									})

							}
						})
					}
				}
				return actions
			},
			descriptor: {
				onModelLoadFirstTime: model => {
					model.on("property:change", (property, value) => {
						if (property === "active"  || property === "roles")  {
							model.invalidateForm()
						}

					})

				},

				areas: [
					{
						title: M("generalInformations"),
						subtitle: null,
						fields: [
							{
								property: "code",
								control: ReadOnlyText,
								label: M("code"),
								placeholder: M("code"),
								size: "col-sm-4"
							},
							{
								property: "name",
								control: Text,
								label: M("name"),
								placeholder: M("name"),
								size: "col-sm-4"
							},
							{
								property: "lastname",
								control: Text,
								label: M("lastname"),
								placeholder: M("lastname"),
								size: "col-sm-4"
							},
							{
								property: "_image",
								control: Image,
								label: M("image")
							}
						]
					},
					{
						title: "Account",
						fields: [
							{
								property: "mail",
								control: Mail,
								label: M("mail"),
								placeholder: M("mailAddress"),
							},
							{
								property: "roles",
								label: M("role"),
								size: "col-sm-6",
								control: EntitiesLookupContainer,
								formatter: v => {
									return v.role
								},
								props: {
									id: "user_roles",
									mode: "multiple",
									entity: "role",
									selectionGrid: {
										columns: [
											{property: "localizedRole", header: M("name"), cell: TextCell}
										]
									},
									popupGrid: {
										columns: [
											{property: "localizedRole", header: M("name"), cell: TextCell}
										]
									}
								}
							},
							{
								property: "active",
								control: YesNo,
								label: M("active"),
							},
						]
					}
				]
			}
		}
	},

	role: {
		grid: {
			title: M("rolesList"),
			subtitle: M("rolesListDescription"),
			quickSearchEnabled: true,
			descriptor: {
				columns: [
	                {property: "role", header: M("role"), cell: TextCell, sortable: true, searchable: true}
	            ]
			}
		},
		form: {
			title: M("editRole"),
			subtitle: null,
			descriptor: {
				showFloatingSaveBtn: true,
				fields: [
					{
                        property: "role",
                        control: Text,
                        label: M("role"),
                        placeholder: M("nameOfRole")
                    },
                    {
                    	property: "_permissions",
                    	label: M("permissions"),
                    	placeholder: M("selectPermissions"),
                    	control: ValuesLookupContainer,
                    	props: {
                    		id: "role_permissions",
                    		collection: "permissions",
	                    	mode: "multiple",
	                        selectionGrid: {
	                            columns: [
	                                {property: "label", header: M("name"), cell: TextCell}
	                            ]
	                        },
	                        popupGrid: {
	                            columns: [
	                                {property: "label", header: M("name"), cell: TextCell}
	                            ]
	                        }
                    	}

					}
				]
			}
		}
	},


	category: {
		grid: {
			title: "Macro/Area/Category",
			quickSearchEnabled: true,
			descriptor: {
				columns: [
	                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
	                {property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
	                {
						property: "parent.description",
						header: M("parent"),
						cell: TextCell,
						visibility: props => {
							const level = optional(() => props.level);
							return level !== 0;
						}
					},
	            ]
			}
		},
		form: {
			title: "Macro/Area/Category",		
			descriptor: {
				fields: [
					{
                        property: "description",
                        control: Text,
                        label: M("description"),
                        placeholder: M("description")
                    },
                    {
                    	property: "parent",
                    	label: M("parent"),
                    	placeholder: M("parent"),
                    	control: EntitiesLookupContainer,
						visibility: model => model.get("level") !== 0,
                    	props: {
                    		id: "category_parent",
                    		entity: "category",
	                    	mode: "single",
							onBeforeLookup: (model, query) => {
								const level = model.get("level");
								if (level) {
									query.eq("level", level - 1).hideFilter("level");
								}
							},
	                        selectionGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "parent.description", header: M("parent"), cell: TextCell, sortable: true, searchable: true},
	                            ]
	                        },
	                        popupGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "parent.description", header: M("parent"), cell: TextCell, sortable: true, searchable: true},
	                            ]
	                        }
                    	}
					},
					{
                        property: "associateToAllStores",
                        control: YesNo,
                        label: M("associateToAllStores"),
                        placeholder: M("associateToAllStores")
                    },
				]
			}
		}
	},


	questionTemplate: {
		grid: {
			title: M("questionsCatalog"),
			quickSearchEnabled: true,
			descriptor: {
				columns: [
	                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
	                {property: "description", header: M("question"), cell: TextCell, sortable: true, searchable: true},
					{property: "ratingType", header: M("ratingType"), cell: TextCell, props: {formatter: v => M(v)}},
	                {property: "category.description", header: M("category"), cell: TextCell},
					{
						property: "allowPhotos",
						header: M("images"),
						cell: YesNoCell,
					},
					{
						property: "allowNotes",
						header: M("notes"),
						cell: YesNoCell,
					},
					{
						property: "active",
						header: M("state"),
						cell: StateCell,
						sortable: true,
						searchable: true,
						searchForm: activeSearchForm
					},
	            ]
			}
		},
		form: {
			descriptor: {
				fields: [
					{
                        property: "description",
                        control: Text,
                        label: M("description"),
                        placeholder: M("description")
                    },
                    {
                    	property: "category",
                    	label: M("category"),
                    	placeholder: M("category"),
                    	control: EntitiesLookupContainer,
                    	props: {
                    		id: "questionTemplate_category",
                    		entity: "category",
	                    	mode: "single",
							onBeforeLookup: (model, query) => {
								query.eq("level", 2);
								query.hideFilter("level");
							},
	                        selectionGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "parent.description", header: M("parent"), cell: TextCell, sortable: true, searchable: true},
	                            ]
	                        },
	                        popupGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "parent.description", header: M("parent"), cell: TextCell, sortable: true, searchable: true},
	                            ]
	                        }
                    	},
					},
					{
						property: "ratingType",
						label: M("ratingType"),
						placeholder: M("ratingType"),
						control: Select,
						props: {
							datasource: datasource.fromEnum(RatingType)
						}
					},
					{
						property: "targetType",
						label: M("targetType"),
						placeholder: M("targetType"),
						control: Select,
						props: {
							datasource: datasource.fromEnum(TargetType)
						}
					},
					{
						property: "target",
						label: M("target"),
						placeholder: M("target"),
						control: Number,
					},
					{
						property: "computationType",
						label: M("computationType"),
						placeholder: M("computationType"),
						control: Select,
						props: {
							datasource: datasource.fromEnum(ComputationType)
						}
					},
					{
                    	property: "relatedQuestionTemplates",
                    	label: M("relatedQuestionTemplates"),
                    	placeholder: M("relatedQuestionTemplates"),
                    	control: EntitiesLookupContainer,
                    	props: {
                    		id: "questionTemplate_relatedQuestionTemplates",
                    		entity: "questionTemplate",
	                    	mode: "multiple",
	                        selectionGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "ratingType", header: M("ratingType"), cell: TextCell, sortable: true, searchable: true},
									{property: "category.description", header: M("category"), cell: TextCell, sortable: true, searchable: true},
								]
	                        },
	                        popupGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "ratingType", header: M("ratingType"), cell: TextCell, sortable: true, searchable: true},
									{property: "category.description", header: M("category"), cell: TextCell, sortable: true, searchable: true},
								]
	                        }
                    	},
					},
					{
						property: "allowPhotos",
						label: M("allowPhotos"),
						placeholder: M("allowPhotos"),
						control: YesNo,
					},
					{
						property: "allowNotes",
						label: M("allowNotes"),
						placeholder: M("allowNotes"),
						control: YesNo,
					},
					{
						property: "readOnly",
						label: M("readOnly"),
						placeholder: M("readOnly"),
						control: YesNo,
					},
					{
						property: "active",
						label: M("active"),
						placeholder: M("active"),
						control: YesNo,
					},
					
				]
			}
		}
	},


	question: {
		grid: {
			title: M("questionsCatalog"),
			quickSearchEnabled: false,
			canEdit: false,
			canCreate: false,
			canDelete: false,
			descriptor: {
				columns: [
	                {property: "template.priority", header: M("priority"), cell: TextCell},
	                {property: "template.description", header: M("description"), cell: TextCell},
					{property: "template.ratingType", header: M("ratingType"), cell: TextCell, props: {formatter: v => M(v)}},
	                {property: "template.category.description", header: M("category"), cell: TextCell},
					{
						property: "template.allowPhotos",
						header: M("images"),
						cell: YesNoCell,
					},
					{
						property: "template.allowNotes",
						header: M("notes"),
						cell: YesNoCell,
					},
					{
						property: "template.active",
						header: M("state"),
						cell: StateCell,
					},
	            ]
			}
		},
		form: {
			descriptor: {
				fields: [
					{
                        property: "template.description",
                        control: Text,
                        label: M("description"),
                        placeholder: M("description")
                    },
                    {
                    	property: "template.category.description",
                    	label: M("category"),
                    	placeholder: M("category"),
                    	control: EntitiesLookupContainer,
                    	props: {
                    		id: "questions_category",
                    		entity: "category",
	                    	mode: "single",
							onBeforeLookup: (model, query) => {
								query.eq("level", 2);
								query.hideFilter("level");
							},
	                        selectionGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "parent.description", header: M("parent"), cell: TextCell, sortable: true, searchable: true},
	                            ]
	                        },
	                        popupGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "parent.description", header: M("parent"), cell: TextCell, sortable: true, searchable: true},
	                            ]
	                        }
                    	},
					},
					{
						property: "template.ratingType",
						label: M("ratingType"),
						placeholder: M("ratingType"),
						control: Select,
						props: {
							datasource: datasource.fromEnum(RatingType)
						}
					},
					{
						property: "template.targetType",
						label: M("targetType"),
						placeholder: M("targetType"),
						control: Select,
						props: {
							datasource: datasource.fromEnum(TargetType)
						}
					},
					{
						property: "template.target",
						label: M("target"),
						placeholder: M("target"),
						control: Number,
					},
					{
						property: "template.computationType",
						label: M("computationType"),
						placeholder: M("computationType"),
						control: Select,
						props: {
							datasource: datasource.fromEnum(ComputationType)
						}
					},
					{
                    	property: "template.relatedQuestionTemplates",
                    	label: M("relatedQuestionTemplates"),
                    	placeholder: M("relatedQuestionTemplates"),
                    	control: EntitiesLookupContainer,
                    	props: {
                    		id: "questionTemplate_relatedQuestionTemplates",
                    		entity: "questionTemplate",
	                    	mode: "multiple",
	                        selectionGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "ratingType", header: M("ratingType"), cell: TextCell, sortable: true, searchable: true},
									{property: "category.description", header: M("category"), cell: TextCell, sortable: true, searchable: true},
								]
	                        },
	                        popupGrid: {
	                            columns: [
	                                {property: "priority", header: M("priority"), cell: TextCell, sortable: true, searchable: true},
									{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
									{property: "ratingType", header: M("ratingType"), cell: TextCell, sortable: true, searchable: true},
									{property: "category.description", header: M("category"), cell: TextCell, sortable: true, searchable: true},
								]
	                        }
                    	},
					},
					{
						property: "template.allowPhotos",
						label: M("allowPhotos"),
						placeholder: M("allowPhotos"),
						control: YesNo,
					},
					{
						property: "template.allowNotes",
						label: M("allowNotes"),
						placeholder: M("allowNotes"),
						control: YesNo,
					},
					{
						property: "template.readOnly",
						label: M("readOnly"),
						placeholder: M("readOnly"),
						control: YesNo,
					},
					{
						property: "template.active",
						label: M("active"),
						placeholder: M("active"),
						control: YesNo,
					},

				]
			}
		}
	},


    revision: {
        grid: {
            title: M("revisions"),
            descriptor: {
                columns: [
                    {property: "code", header: M("code"), cell: TextCell, sortable: false, searchable: false},
                    {property: "type", header: M("type"), cell: TextCell, sortable: false, searchable: false},
                    {
                        property: "creator",
                        header: M("author"),
                        cell: TextCell,
                        sortable: false,
                        searchable: false
                    },

                    {
                        property: "dateToString",
                        header: M("date"),
                        cell: TextCell,
                        sortable: false,
                        searchable: false
                    },
                    {
                        property: "differences",
                        header: M("differences"),
                        cell: MultiTextCell,
                        sortable: false,
                        searchable: false,
                        props: {
                            singleItemFormatter(v) {
                                let previousValueString = "";
                                let newValueString = "";
                                previousValueString = M("previousValue") + ": " + (v.previousValueDescription? v.previousValueDescription : " null ") + ", ";
                                newValueString = M("newValue") + ": " + (v.newValueDescription? v.newValueDescription : " null ");
                                return M(v.name) + " -> " + previousValueString + newValueString
                            }
                        }
                    }

                ]
            }
        },
	},

	storeType: {
		grid: {
			quickSearchEnabled: true,
			title: M("storeTypes"),
			descriptor: {
				columns: [
					{property: "code", header: M("code"), cell: TextCell, sortable: true, searchable: true, searchForm: SEARCH_FORM_CODE_DESCRIPTOR()},
					{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
				]
			}
		},
		form: {
			descriptor: {				
				areas: [
					{
						title: null,
						subtitle: null,
						fields: [
							{
								property: "code",
								control: ReadOnlyText,
								label: M("code"),
								placeholder: M("code"),
								size: "col-sm-4"
							},
							{
								property: "description",
								control: Text,
								label: M("description"),
								placeholder: M("description"),
								size: "col-sm-4"
							},
						]
					},
				]
			}
		}
	},
	
	store: {
		grid: {
			createUrl: "/entities/store/new/header",
			editUrl: (data) => `/entities/store/${data.id}/header`,
			quickSearchEnabled: true,
			title: M("stores"),
			generateTableExtraHeader: () => {
				return <RetailersGridHeader/>
			   },
			descriptor: {
				columns: [
					{property: "code", header: M("code"), cell: TextCell, sortable: true, searchable: true, searchForm: SEARCH_FORM_CODE_DESCRIPTOR()},
					{property: "description", header: M("store"), cell: TextCell, sortable: true, searchable: true},
					{property: "storeType", header: M("type"), cell: TextCell, props: {formatter: v => v && v.description}},
					{property: "storeAddress", header: M("address"), cell: StoreAddressCell},
					{property: "totalSurveys", header: M("totalSurveys"), cell: TextCell},
					{property: "openSurveyAssociated", header: M("ongoingSurveys"), cell: TextCell, props: {formatter: v => v ? M("yes") : M("no")}},
					{property: "currentSurvey.lastEditTime", header: M("lastSurvey"), cell: DateCell, props: {format: DATE_FORMAT_FULL}},
					{property: "lastScoreValue", header: M("lastScore"), cell: ArrowTrend},
				]
			}
		},
		form: {
			gridUrl: "/entities/store?showSidebar=false",
			descriptor: {
				areas: [
					{
						title: M("generalInformations"),
						subtitle: null,
						fields: [
							{
								property: "code",
								control: ReadOnlyText,
								label: M("code"),
								placeholder: M("code"),
							},
							{
								property: "description",
								control: Text,
								label: M("description"),
								placeholder: M("description"),
							},
							{
								property: "storeType",
								label: M("type"),
								control: EntitiesLookupContainer,
								formatter: (v) => {
									return v ? v.description : "";
								},
								props: {
									id: "store_types",
									mode: "single",
									entity: "storeType",
									selectionGrid: {
										columns: [
											{property: "description", header: M("name"), cell: TextCell}
										]
									},
									popupGrid: {
										columns: [
											{property: "description", header: M("name"), cell: TextCell}
										]
									}
								}
							},
							{
								property: "municipality",
								control: Text,
								label: M("municipality"),
								placeholder: M("municipality"),
							},
							{
								property: "province",
								control: Text,
								label: M("province"),
								placeholder: M("province"),
							},
							{
								property: "address",
								control: Text,
								label: M("address"),
								placeholder: M("address"),
							},
						]
					},
					{
						title: M("allowedCategories"),
						fields: [
							{
								property: "allowedCategories",
								control: CategoriesTreeSelectorControl
							}
						]
					}
				]
			}
		}
	},

	surveyTemplate: {
		grid: {
			quickSearchEnabled: true,
			title: M("surveyTemplates"),
			editUrl: (data) => `/entities/surveyTemplate/${data.id}/header`,
			descriptor: {
				columns: [
					{property: "code", header: M("code"), cell: TextCell, sortable: true, searchable: true, searchForm: SEARCH_FORM_CODE_DESCRIPTOR()},
					{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
					{property: "totalMacroareas", header: M("macroareas"), cell: TextCell},
					{property: "totalAreas", header: M("areas"), cell: TextCell},
					{property: "totalCategories", header: M("categories"), cell: TextCell},
					{property: "allowedStoreIds", header: M("allowedStores"), cell: TextCell, props: {formatter: v => optional(() => v.length, 0)}},
					{property: "totalSurveys", header: M("evaluations"), cell: TextCell},
					{
						property: "active",
						header: M("state"),
						cell: StateCell,
						sortable: true,
						searchable: true,
						searchForm: activeSearchForm
					},				
				]
			}
		},
		form: {
			
			descriptor: {
				areas: [
					{
						title: M("generalInformations"),
						subtitle: null,
						fields: [
							{
								property: "code",
								control: ReadOnlyText,
								label: M("code"),
								placeholder: M("code"),
							},
							{
								property: "description",
								control: Text,
								label: M("description"),
								placeholder: M("description"),
							},
							{
								property: "allowedStores",
								label: M("allowedStores"),
								control: EntitiesLookupContainer,
								props: {
									id: "surveyTemplate_allowedStores",
									entity: "store",
									mode: "multiple",
									selectionGrid: {
										columns: [
											{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
										]
									},
									popupGrid: {
										columns: [
											{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
										]
									}
								},
							},
							{
								property: "active",
								control: YesNo,
								label: M("active"),
							},
						],

					},
				]
			}
		},

	},

	presentationConfiguration: {
		grid: {
			initialQuery: new Query().sort("exportTime", true).setPage(1).setRowsPerPage(50),

			quickSearchEnabled: true,
			title: M("presentationConfigurationList"),
			description: M("presentationConfigurationListDescription"),
			descriptor: {
				columns: [
					{property: "exportTime", header: M("exportDate"), cell: TextCell, sortable: true, searchable: false, props: { formatter: v => moment(v).fromNow() }},
					{property: "survey.code", header: M("code"), cell: TextCell, sortable: true, searchable: false},
					{property: "store.description", header: M("store"), cell: TextCell, sortable: true, searchable: false},
					{property: "surveyTemplate.description", header: M("survey"), cell: TextCell, sortable: true, searchable: false},
					{property: "user.mail", header: M("user"), cell: TextCell, sortable: true, searchable: false},
				]
			}
		},
		form: {
			getActions: (data) => {
				if (data?.id) {
					return [
						{
							id: "download",
							type: "button",
							icon: null,
							tooltip: M("download"),
							text: M("download"),
							action: () => {
								window.open(getFileServerUrl(data.path));
							}
						},
					];
				} else {
					return ["save"];
				}
			},
			descriptor: {

				areas: [
					{
						title: null,
						subtitle: null,
						fields: [
							{
								property: "survey",
								label: M("survey"),
								control: EntitiesLookupContainer,
								formatter: v => optional(() => v.code, "NA"),
								props: {
									initialQuery: new Query().eq("status", "closed").hideFilter("status").sort("lastEditTime", true).setPage(1).setRowsPerPage(50),
									id: "presentationConfiguration_survey",
									mode: "single",
									entity: "survey",
									selectionGrid: {
										columns: [
											{property: "code", header: M("code"), cell: TextCell, sortable: true, searchable: false},
											{property: "store.storeType.description", header: M("type"), cell: TextCell, sortable: true, searchable: false},
											{property: "store.description", header: M("store"), cell: TextCell, sortable: true, searchable: false},
											{property: "surveyTemplate.description", header: M("survey"), cell: TextCell, sortable: true, searchable: false},
											{property: "status", header: M("status"), cell: TextCell, sortable: true, searchable: false},
											{property: "lastScore.value", header: M("score"), cell: TextCell, sortable: true, searchable: false},
											{property: "lastEditTime", header: M("lastEditDate"), cell: TextCell, sortable: true, searchable: false, props: {formatter: v => moment(v).format(M("dateTimeFormat"))}},
										]
									},
									popupGrid: {
										columns: [
											{property: "code", header: M("code"), cell: TextCell, sortable: true, searchable: false},
											{property: "store.storeType.description", header: M("type"), cell: TextCell, sortable: true, searchable: false},
											{property: "store.description", header: M("store"), cell: TextCell, sortable: true, searchable: false},
											{property: "surveyTemplate.description", header: M("survey"), cell: TextCell, sortable: true, searchable: false},
											{property: "status", header: M("status"), cell: TextCell, sortable: true, searchable: false},
											{property: "lastScore.value", header: M("score"), cell: TextCell, sortable: true, searchable: false, props: {formatter: v => parseFloat(v).toFixed(2)}},
											{property: "lastEditTime", header: M("lastEditDate"), cell: TextCell, sortable: true, searchable: false, props: {formatter: v => moment(v).format(M("dateTimeFormat"))}},

										]
									}
								}
							},
							{
								property: "exportTime",
								control: ReadOnlyText,
								label: M("exportDate"),
								placeholder: M("exportDate"),
								props: { formatter: v => moment(v).fromNow() }
							},
							{
								property: "user.mail",
								control: ReadOnlyText,
								label: M("user"),
								placeholder: M("user"),
							},

						]
					},
					{
						title: M("notes"),
						fields: [
							{
								property: "presentationNotes",
								control: PresentationNotesControl
							}
						]
					}
				]
			}
		}
	},

	trendConfiguration: {
		grid: {
			initialQuery: new Query().sort("exportTime", true).setPage(1).setRowsPerPage(50),

			quickSearchEnabled: true,
			title: M("trendConfigurationList"),
			description: M("trendConfigurationListDescription"),
			descriptor: {
				columns: [
					{property: "exportTime", header: M("exportDate"), cell: TextCell, sortable: true, searchable: false, props: { formatter: v => moment(v).fromNow() }},
					{property: "from", header: M("from"), cell: DateCell, sortable: true, searchable: false},
					{property: "to", header: M("to"), cell: DateCell, sortable: true, searchable: false},
					{property: "user.mail", header: M("user"), cell: TextCell, sortable: true, searchable: false},
				]
			}
		},
		form: {
			getActions: (data) => {
				if (data?.id) {
					return [
						{
							id: "download",
							type: "button",
							icon: null,
							tooltip: M("download"),
							text: M("download"),
							action: () => {
								window.open(getFileServerUrl(data.path));
							}
						},
					];
				} else {
					return ["save"];
				}
			},
			descriptor: {

				areas: [
					{
						title: null,
						subtitle: null,
						fields: [
							{
								property: "surveyTemplate",
								label: M("surveyTemplate"),
								control: EntitiesLookupContainer,
								props: {
									id: "trendConfiguration_surveyTemplate",
									entity: "surveyTemplate",
									mode: "single",
									selectionGrid: {
										columns: [
											{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
										]
									},
									popupGrid: {
										columns: [
											{property: "description", header: M("description"), cell: TextCell, sortable: true, searchable: true},
										]
									}
								},
							},
							{
								property: "from",
								control: DateTime,
								label: M("from"),
								placeholder: M("from"),
							},
							{
								property: "to",
								control: DateTime,
								label: M("to"),
								placeholder: M("to"),
							},
							{
								property: "exportTime",
								control: ReadOnlyText,
								label: M("exportDate"),
								placeholder: M("exportDate"),
								props: { formatter: v => moment(v).fromNow() }
							},
							{
								property: "user.mail",
								control: ReadOnlyText,
								label: M("user"),
								placeholder: M("user"),
							},

						]
					},
				]
			}
		}
	},

	survey: {
		grid: {
			quickSearchEnabled: true,
			title: M("evaluations"),
			canEdit: false,
			canCreate: false,
			canDelete: false,
			generateTableExtraHeader: () => {
				return <EvaluationsGridHeader/>
			   },
			descriptor: {
				columns: [
					{property: "code", header: M("code"), cell: TextCell, sortable: true, searchable: true},
					{property: "store.description", header: M("store"), cell: TextCell},
					{property: "store", header: M("address"), cell: TextCell, props: {formatter: v => v ? formatStoreAddress(v) : ""}},
					{property: "surveyTemplate.description", header: M("surveyTemplate"), cell: TextCell},
					{property: "lastScore", header: M("observations"), cell: TextCell, props: {formatter: v => v ? formatObservations(v) : "0"}},
					{property: "user.fullName", header: M("author"), cell: TextCell, sortable: true, searchable: true, props: {formatter: v => v ?? "-"}},
					{property: "status", header: M("state"), cell: TextCell, props: {formatter: v => v === "closed" ? M("completed") : M("ongoing")}},
				]
			}
		},
		form: {
			descriptor: {
				areas: [
					{
						fields: [
							{
								property: "code",
								control: ReadOnlyText,
								label: M("code"),
								placeholder: M("code"),
							},
							{
								property: "store.description",
								control: ReadOnlyText,
								label: M("store"),
								placeholder: M("store"),
							},
							{
								property: "store",
								label: M("address"),
								control: ReadOnlyText,
								placeholder: M("address"),
								props: {formatter: v => v ? formatStoreAddress(v) : ""},
							},
							{
								property: "surveyTemplate.description",
								label: M("description"),
								control: ReadOnlyText,
								placeholder: M("surveyTemplate"),
							},
							{
								property: "lastScore",
								label: M("observations"),
								control: ReadOnlyText,
								placeholder: M("observations"),
								props: {formatter: v => v ? formatObservations(v) : "0"},
							},
							{
								property: "user.fullName",
								label: M("user"),
								control: ReadOnlyText,
								placeholder: M("user"),
							},
							{
								property: "status",
								label: M("status"),
								control: ReadOnlyText,
								placeholder: M("status"),
							},
						],

					},
				]
			}
		}
	},
}

export default entities

export function getEntityData(entityName) {
    const data = entities[entityName];
    if (data) {
        return data;
    } else {
        new Error("[entities] getData: could not find entity data");
    }
}

export function withEntityDescriptor(Component) {
    class WrappedComponent extends React.Component {

        getEntityData() {
            const entity = this.props.entity;
            if (isEmpty(entity)) {
                throw new Error("[entities] withEntityDescriptor: entity prop is not defined")
            }
            return getEntityData(entity);
        }

        render() {
            const descriptorData = this.getEntityData(),
                entityGrid = descriptorData.grid,
                entityForm = descriptorData.form;

            return(
                <Component
                    entityGrid={entityGrid}
                    entityForm={entityForm}
                    {...this.props}
                />
            )
        }
    }

    return WrappedComponent
}